import type { ChangeEvent } from "react";

import { adminUiCopy } from "@/copy/adminUiCopy";
import { MAX_DOCS_SHARE_EXPIRY_DAYS } from "@/pages/docsShareLinks/docsShareLinksUtils";
import {
  VNextButton,
  VNextCard,
  VNextField,
  VNextInput,
} from "@/vnext/primitives";

interface DocsSearchCardProps {
  searchInput: string;
  expiresInDays: number | "";
  includeArchived: boolean;
  searchErrorMessage: string | null;
  debouncedSearch: string;
  isSearching: boolean;
  matchedPaths: string[];
  isCreatingShareLink: boolean;
  onSearchInputChange: (value: string) => void;
  onExpiresInDaysChange: (value: number | "") => void;
  onIncludeArchivedChange: (checked: boolean) => void;
  onGenerateShareLink: (docPath: string) => void;
}

const handleExpiryInputChange = (
  event: ChangeEvent<HTMLInputElement>,
  onExpiresInDaysChange: (value: number | "") => void,
) => {
  const value = event.target.value;
  if (!value.trim()) {
    onExpiresInDaysChange("");
    return;
  }

  const parsed = Number(value);
  if (Number.isFinite(parsed)) {
    onExpiresInDaysChange(parsed);
  }
};

export function DocsSearchCard({
  searchInput,
  expiresInDays,
  includeArchived,
  searchErrorMessage,
  debouncedSearch,
  isSearching,
  matchedPaths,
  isCreatingShareLink,
  onSearchInputChange,
  onExpiresInDaysChange,
  onIncludeArchivedChange,
  onGenerateShareLink,
}: DocsSearchCardProps) {
  return (
    <VNextCard
      subtitle="Search by doc path or filename, then issue a short-lived public link for the exact document you need to share."
      title="Search docs"
    >
      <div className="docs-share-links-route__search-layout">
        <VNextField
          hint="Type at least 2 characters. Matching is case-insensitive. Full repo paths like docs/plans/... also fall back to filename matches."
          label="Doc path search"
        >
          <VNextInput
            onChange={(event) => onSearchInputChange(event.target.value)}
            value={searchInput}
          />
        </VNextField>
        <VNextField
          hint={`1-${MAX_DOCS_SHARE_EXPIRY_DAYS} days. ${adminUiCopy.hoverHelp.docsShare.expiresAt}`}
          label="Expiry (days)"
        >
          <VNextInput
            inputMode="numeric"
            max={MAX_DOCS_SHARE_EXPIRY_DAYS}
            min={1}
            onChange={(event) =>
              handleExpiryInputChange(event, onExpiresInDaysChange)
            }
            type="number"
            value={expiresInDays}
          />
        </VNextField>
      </div>

      <label className="docs-share-links-route__checkbox">
        <input
          checked={includeArchived}
          onChange={(event) => onIncludeArchivedChange(event.target.checked)}
          type="checkbox"
        />
        <span>Include archived docs (scratchpads)</span>
      </label>
      <div className="vnext-muted">
        {adminUiCopy.hoverHelp.docsShare.archivedDocs}
      </div>

      {searchErrorMessage ? (
        <div className="vnext-notice vnext-notice--danger">
          <div className="vnext-notice__content">{searchErrorMessage}</div>
        </div>
      ) : null}

      {debouncedSearch.length < 2 ? (
        <div className="vnext-empty">
          Enter at least 2 characters to search docs.
        </div>
      ) : isSearching ? (
        <div className="vnext-empty">Searching docs…</div>
      ) : matchedPaths.length === 0 ? (
        <div className="vnext-empty">No matching docs found.</div>
      ) : (
        <div className="docs-share-links-route__search-results">
          {matchedPaths.map((docPath) => (
            <div
              className="docs-share-links-route__search-result"
              key={docPath}
            >
              <p className="docs-share-links-route__path">{docPath}</p>
              <VNextButton
                disabled={isCreatingShareLink}
                onClick={() => {
                  onGenerateShareLink(docPath);
                }}
                title={adminUiCopy.hoverHelp.docsShare.createNewLink}
                tone="primary"
              >
                Generate share link
              </VNextButton>
            </div>
          ))}
        </div>
      )}
    </VNextCard>
  );
}
